import React from "react";
import { Loader2 } from "lucide-react";

type Tone = "brand" | "success" | "warning" | "danger" | "neutral" | "info";

const toneClasses: Record<Tone, string> = {
  brand:
    "border-brand-200 bg-brand-50 text-brand-700 dark:border-brand-900/40 dark:bg-brand-900/20 dark:text-brand-300",
  success:
    "border-success-border bg-success-soft text-success",
  warning:
    "border-warning-border bg-warning-soft text-warning",
  danger:
    "border-danger-border bg-danger-soft text-danger",
  neutral:
    "border-border-subtle bg-bg-elevated text-text-secondary",
  info:
    "border-info-border bg-info-soft text-info",
};

function cx(...parts: Array<string | false | null | undefined>) {
  return parts.filter(Boolean).join(" ");
}

export interface PageHeaderAction {
  label: string;
  icon?: React.ReactNode;
  onClick?: () => void;
  variant?: "primary" | "secondary";
  disabled?: boolean;
}

export interface PageHeaderProps {
  breadcrumbs?: React.ReactNode;
  eyebrow?: React.ReactNode;
  title: React.ReactNode;
  description?: React.ReactNode;
  meta?: React.ReactNode;
  actions?: PageHeaderAction[];
  actionsSlot?: React.ReactNode;
  className?: string;
}

export const PageHeader: React.FC<PageHeaderProps> = ({
  breadcrumbs,
  eyebrow,
  title,
  description,
  meta,
  actions,
  actionsSlot,
  className,
}) => (
  <section className={cx("relative", className)}>
    {breadcrumbs && <div className="mb-2.5 text-xs text-text-muted">{breadcrumbs}</div>}
    <div className="flex flex-wrap items-start justify-between gap-3">
      <div className="min-w-0 flex-1">
        {eyebrow ? <div className="sr-only">{eyebrow}</div> : null}
        <h1 className="mt-1 text-[clamp(1.375rem,1.1vw,1.875rem)] font-bold tracking-[-0.035em] text-text-primary">
          {title}
        </h1>
        {description && (
          <p className="mt-1.25 max-w-4xl text-sm leading-5.5 text-text-secondary sm:text-[14px]">
            {description}
          </p>
        )}
        {meta && <div className="mt-3 flex flex-wrap items-center gap-2 text-xs">{meta}</div>}
      </div>
      {(actionsSlot || (actions && actions.length > 0)) && (
        <div className="flex flex-wrap items-center gap-2 self-start pt-0.5">
          {actionsSlot}
          {(actions ?? []).map((action) => (
            <button
              key={action.label}
              type="button"
              onClick={action.onClick}
              disabled={action.disabled}
              className={cx(
                "inline-flex min-h-10 cursor-pointer items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50",
                action.variant === "primary"
                  ? "bg-brand-600 text-white shadow-sm hover:bg-brand-700"
                  : "border border-border-subtle bg-bg-surface text-text-secondary hover:bg-bg-elevated",
              )}
            >
              {action.icon}
              {action.label}
            </button>
          ))}
        </div>
      )}
    </div>
  </section>
);

export const SectionCard: React.FC<React.PropsWithChildren<{
  title?: React.ReactNode;
  description?: React.ReactNode;
  action?: React.ReactNode;
  className?: string;
  bodyClassName?: string;
}>> = ({ title, description, action, className, bodyClassName, children }) => (
  <section className={cx("rounded-xl border border-border-subtle bg-bg-surface p-5 shadow-[0_1px_2px_rgba(15,23,42,0.03)] sm:p-6", className)}>
    {(title || description || action) && (
      <div className="mb-4 flex items-start justify-between gap-4">
        <div className="min-w-0">
          {title && <h2 className="text-base font-semibold text-text-primary">{title}</h2>}
          {description && <p className="mt-0.5 text-xs text-text-muted">{description}</p>}
        </div>
        {action}
      </div>
    )}
    <div className={bodyClassName}>{children}</div>
  </section>
);

export const StatusPill: React.FC<React.PropsWithChildren<{
  tone?: Tone;
  className?: string;
}>> = ({ tone = "neutral", className, children }) => (
  <span className={cx("inline-flex items-center gap-1 rounded border px-2 py-0.5 text-[10px]", toneClasses[tone], className)}>
    {children}
  </span>
);

export interface EmptyStateProps {
  icon?: React.ReactNode;
  title: React.ReactNode;
  description?: React.ReactNode;
  action?: React.ReactNode;
  className?: string;
}

export const EmptyState: React.FC<EmptyStateProps> = ({
  icon,
  title,
  description,
  action,
  className,
}) => (
  <div className={cx("rounded-xl border border-dashed border-border-subtle bg-bg-base px-4 py-10 text-center", className)}>
    {icon && <div className="mx-auto mb-3 flex h-10 w-10 items-center justify-center text-text-muted opacity-60">{icon}</div>}
    <p className="text-sm font-medium text-text-secondary">{title}</p>
    {description && <p className="mx-auto mt-1 max-w-md text-xs leading-5 text-text-muted">{description}</p>}
    {action && <div className="mt-4">{action}</div>}
  </div>
);

/** 统一的加载占位：旋转图标 + 文案。用于卡片/表格内的异步加载态。 */
export const LoadingState: React.FC<{
  label?: React.ReactNode;
  className?: string;
}> = ({ label = "加载中…", className }) => (
  <div
    role="status"
    className={cx("flex flex-col items-center justify-center gap-2 py-12 text-text-muted", className)}
  >
    <Loader2 className="h-5 w-5 animate-spin" />
    <span className="text-sm">{label}</span>
  </div>
);
